import type { CookieJar } from './cookies';
import { SCALEO_PARAM_NAMES } from './sessionAttributionTracking/scaleoClickParams';

/**
 * Param names persisted to / read from the `tracking` session cookie. Kept in
 * one place so the write side (the `utm` tag) and the read side use the exact
 * same scheme and cannot drift apart.
 *
 * Scaleo's `clkid` / `clktype` are deliberately absent: they are session-scoped
 * and owned by `sessionAttributionTracking` (CORE-4767). This cookie outlives
 * the session, so holding them here over-attributed later organic visits.
 */
export const TRACKING_PARAM_MATCHER =
  /(utm|gad)_([a-z]*)|source|gclid|fbclid|buid|lid|wbraid/;

/**
 * Read every tracking param available for the current visit — URL first, with
 * the `tracking` session cookie as fallback. URL names are filtered by
 * `TRACKING_PARAM_MATCHER` (the same scheme the cookie is written with); cookie
 * keys are already write-filtered so they are taken as-is, apart from the Scaleo
 * pair that cookies written before CORE-4767 still carry. Duplicate URL keys
 * resolve to the first occurrence. Values are returned verbatim (no case change).
 */
export function getAllTrackingParams(
  cookies: CookieJar,
  search = '',
): Record<string, string> {
  const out: Record<string, string> = {};

  const urlParams = new URLSearchParams(search);
  for (const [name, value] of urlParams.entries()) {
    if (name in out) continue; // first occurrence wins
    if (TRACKING_PARAM_MATCHER.test(name)) out[name] = value;
  }

  let cookieParams: URLSearchParams;
  try {
    cookieParams = new URLSearchParams(cookies.get('tracking')?.value ?? '');
  } catch {
    cookieParams = new URLSearchParams();
  }
  for (const [name, value] of cookieParams.entries()) {
    if (name in out) continue; // URL takes precedence over cookie
    // Ignore a stale Scaleo pair left in cookies issued by an earlier build;
    // the `utm` tag strips it on the next page load.
    if (SCALEO_PARAM_NAMES.includes(name)) continue;
    out[name] = value;
  }

  return out;
}
